import * as React from 'react';
import { Select, MenuItem } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/styles';

import da from 'static/img/country-flags/dk.svg';
import en from 'static/img/country-flags/en.svg';
import sv from 'static/img/country-flags/sv.svg';
import nb from 'static/img/country-flags/nb.svg';
import is from 'static/img/country-flags/is.svg';
import fi from 'static/img/country-flags/fi.svg';
import de from 'static/img/country-flags/de.svg';
import es from 'static/img/country-flags/es.svg';
import el from 'static/img/country-flags/el.svg';
import fr from 'static/img/country-flags/fr.svg';
import nl from 'static/img/country-flags/nl.svg';
import ro from 'static/img/country-flags/ro.svg';

import { RootState } from 'redux/rootReducer';
import { SettingsActions } from 'redux/ducks/settings';
import { ErrorActions } from 'redux/ducks/error';
import SVGInline from 'react-svg-inline';

export const SUPPORTED_LOCALES = [
  'da',
  'nb',
  'sv',
  'en',
  'is',
  'fi',
  'de',
  'es',
  'el',
  'fr',
  'nl',
  'ro'
] as const;
const flags = { da, nb, sv, en, is, fi, de, es, el, fr, nl, ro } as const;

const useStyles = makeStyles({
  select: {
    width: '100%',
    zIndex: 2300
  },
  selectCompact: {
    zIndex: 'initial'
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'row nowrap',
    minWidth: '100px'
  },
  image: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '30px',
    width: '30px',
    marginRight: '15px',
    paddingRight: '-4px'
  },
  imageMobile: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '25px',
    width: '25px',
    marginRight: '25px'
  },
  icon: {},
  text: {},
  menuItemCompact: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'row nowrap',
    minWidth: 'initial',
    width: '22px'
  }
});

type StoreProps = ReturnType<typeof mapStateToProps>;

interface OwnProps {
  // if value is provided, store is not used; a bit of a quick fix;
  // should extract out store usage to parent
  value?: string;
  onChange: (locale: string) => Promise<SettingsActions | ErrorActions>;
  className?: string;
  classes?: ClassesOverride<typeof useStyles>;
  name?: string;
  size?: 'small';
  compact?: boolean;
}

export type LanguageSwitcherProps = StoreProps & OwnProps;

const LanguageSwitcher: React.FunctionComponent<LanguageSwitcherProps> = (props) => {
  const classes = useStyles(props);
  const intl = useIntl();
  const { onChange, locale, className, name, size, compact } = props;

  /**
   * Calls the onChange handler received from the parent component with the chosen language string
   * @param { React.MouseEvent<HTMLElement> } e: Mouse Event
   * @param { number } value: Index value used to return the right property
   * @public
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.stopPropagation();
    e.preventDefault();
    void onChange(e.target.value);
  };

  return (
    <Select
      className={classNames(classes.select, { [classes.selectCompact]: compact }, className)}
      disableUnderline
      value={locale}
      onChange={handleChange}
      name={name}
    >
      {SUPPORTED_LOCALES.map((locale, i) => (
        <MenuItem key={i} value={locale}>
          <div
            className={classNames(classes.menuItem, {
              [classes.menuItemCompact]: compact
            })}
          >
            <div className={classes.icon}>
              <SVGInline
                className={size === 'small' ? classes.imageMobile : classes.image}
                svg={flags[locale]}
              />
            </div>
            {!compact && (
              <div className={classes.text}>
                {intl.formatMessage({ id: `languages.${locale}` })}
              </div>
            )}
          </div>
        </MenuItem>
      ))}
    </Select>
  );
};

const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  locale: ownProps.value || state.settings.locale
});

export default connect<StoreProps, unknown, OwnProps>(mapStateToProps)(LanguageSwitcher);
